import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { untilDestroyed } from '@ngneat/until-destroy';

import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import {
  BlazeFormValidators,
  ButtonBarComponent,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';
import { WebhookIdInputComponent } from '../../webhook-id-input/webhook-id-input.component';

import { ConnectorForm, TwilioConfiguration } from './twilio-connector-dialog.models';

@Component({
  selector: 'blaze-twilio-connector-dialog',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    CopyConnectorConfigEnvButtonComponent,
    DialogModule,
    EnvironmentIndicatorComponent,
    MatIconModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    PanelComponent,
    WebhookIdInputComponent,
    FormsModule,
  ],
  templateUrl: './twilio-connector-dialog.component.html',
  styleUrls: ['./twilio-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'data-connector-dialog' },
})
export class TwilioConnectorDialogComponent
  extends BaseConnectorDialogComponent<TwilioConfiguration>
  implements OnInit
{
  private readonly panelRef = inject(PanelRef);

  protected readonly webhookBaseUrl = '/twilio/webhook';

  serviceName = 'Twilio';
  connectorForm: FormGroup<ConnectorForm>;
  isLoading = false;

  constructor() {
    super();
    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      twilioAccountId: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      authToken: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      phoneNumber: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, BlazeFormValidators.phoneNumber],
      }),
      useWebhook: new FormControl(false),
      webhookId: new FormControl(''),
    });

    this.initUseWebhookCleanup(this.connectorForm);
  }

  ngOnInit(): void {
    if (this.connector) {
      this.fillForm(this.getConfiguration(this.connector));
    }
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, twilioAccountId, authToken, phoneNumber, webhookId } =
      this.connectorForm.value;

    if (!connectorName || !twilioAccountId || !authToken || !phoneNumber) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        twilioAccountId,
        authToken,
        phoneNumber,
        id: webhookId ?? '',
        url: webhookId ? this.connectorsService.makeWebhookUrl(this.webhookBaseUrl, webhookId) : '',
      },
    );

    this.isLoading = true;
    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.panelRef.close();
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  getConfiguration(connector?: ConnectorConfiguration): TwilioConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? {};

    const twilioAccountId = String(configuration?.twilioAccountId ?? '');
    const authToken = String(configuration?.authToken ?? '');
    const phoneNumber = String(configuration?.phoneNumber ?? '');
    const id = String(configuration?.id ?? '');
    const url = String(configuration?.url ?? '');

    return {
      connectorId,
      connectorName,
      twilioAccountId,
      authToken,
      phoneNumber,
      id,
      url,
    };
  }

  fillForm(configuration: TwilioConfiguration): void {
    const { connectorId, connectorName, twilioAccountId, authToken, phoneNumber, id } =
      configuration;

    const useWebhook = !!id;

    this.connectorId = connectorId;

    this.connectorForm.patchValue(
      {
        connectorName,
        twilioAccountId,
        authToken,
        phoneNumber,
        useWebhook,
        webhookId: id,
      },
      { emitEvent: false },
    );
  }
}
